import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { VenueProvider, useVenue } from "@/components/shared/VenueContext";
import { PlanProvider, usePlan, PLAN_FEATURES, PLAN_LABELS, ROLE_LABELS, MODULES } from "@/components/shared/PlanGuard";
import QuickLoader from "@/components/shared/QuickLoader";
import AIAssistant from "@/components/assistant/AIAssistant";
import {
  LayoutDashboard, Clock, Thermometer, Droplet, SprayCan,
  AlertTriangle, ChefHat, Download, Settings, Building2,
  LayoutTemplate, ChevronDown, LogOut, Menu, X, FileText,
  Bell, ClipboardCheck, BarChart3, Store, ShieldCheck,
  LineChart, ChevronRight, ShoppingCart, Sparkles, Scale,
  Shield, ShieldAlert, Lock, Users, CreditCard, Zap, User,
  Bug, Wrench, Truck, Gauge, Bot, Recycle,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import hosys from "@/api/api";
import { TopHeader, DesktopSidebar, MobileSlideOut, MobileBottomNav } from "@/components/layout/LayoutParts";
import ProfileDialog from "@/components/shared/ProfileDialog";

// Superadmin emails - always have full access
const SUPERADMIN_EMAILS = ["upp470@gmail.com"];

// ─── Icon map per module ──────────────────────────────────────────────────────
const MODULE_ICONS = {
  dashboard:            LayoutDashboard,
  time_control:         Clock,
  temperatures:         Thermometer,
  chlorine:             Droplet,
  cleaning:             SprayCan,
  incidents:            AlertTriangle,
  documents:            FileText,
  downloads:            Download,
  alerts:               Bell,
  verifications:        ClipboardCheck,
  recipe_sheets:        ChefHat,
  ingredients:          ShoppingCart,
  compliance_dashboard: BarChart3,
  appcc:                Shield,
  rgpd:                 Shield,
  prl:                  AlertTriangle,
  harassment:           ShieldAlert,
  analytics:            LineChart,
  ai_generator:         Sparkles,
  regulatory:           Scale,
  technical_compliance:  ShieldCheck,
  electrical_compliance: Zap,
  my_hours:             Clock,
  venue_profile:        Store,
  team:                 Users,
  invoice_scanner:      FileText,
  pest_control:         Bug,
  maintenance:          Wrench,
  traceability:         Truck,
  risk_dashboard:       Gauge,
  ai_assistant:         Bot,
  waste_management:     Recycle,
};

// Modules visible to workers (limited view) — workers go to WorkerApp directly
// Default if no specific permissions are set on the member
const WORKER_VISIBLE_DEFAULT = ["time_control", "my_hours"];

// Always visible to all workers regardless of permissions
const WORKER_ALWAYS_VISIBLE = ["dashboard", "time_control", "my_hours"];

// Modules visible to auditors
const AUDITOR_VISIBLE = ["dashboard", "temperatures", "cleaning", "incidents", "documents", "verifications", "compliance_dashboard", "appcc", "analytics"];

const ADMIN_ITEMS = [
  { name: "Admin Locals",     page: "AdminVenues",    icon: Building2 },
  { name: "Plantilles",       page: "Templates",      icon: LayoutTemplate },
  { name: "Revisió Costos",   page: "CostReview",     icon: BarChart3 },
  { name: "Configuració",     page: "VenueSettings",  icon: Settings },
  { name: "Config. Alertes",  page: "AlertSettings",  icon: Bell },
  { name: "Plataforma AEH",   page: "PlatformAdmin",  icon: ShieldCheck },
  { name: "Analítica Global", page: "Analytics",      icon: BarChart3 },
  { name: "Subscripció",      page: "SubscriptionManagement", icon: CreditCard },
];

function SectionLabel({ label }) {
  return (
    <div className="px-3 pt-4 pb-1">
      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600">{label}</p>
    </div>
  );
}

function NavItem({ name, page, icon: Icon, isActive, onClick, badge }) {
  return (
    <Link
      to={createPageUrl(page)}
      onClick={onClick}
      className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 relative ${
        isActive
          ? "bg-[#7B2D3F] text-white shadow-md shadow-[#7B2D3F]/30"
          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
      }`}
    >
      <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-white" : ""}`} />
      <span className="flex-1 truncate">{name}</span>
      {badge > 0 && (
        <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 flex-shrink-0">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
      {isActive && <span className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white/40 rounded-l-full" />}
    </Link>
  );
}

function LockedNavItem({ name, icon: Icon }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-500 cursor-not-allowed select-none">
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span className="flex-1 truncate">{name}</span>
      <Lock className="w-3 h-3 flex-shrink-0" />
    </div>
  );
}

// ─── Dynamic nav builder ──────────────────────────────────────────────────────
function buildNav(userRole, subscription, memberPermissions) {
  const plan = subscription?.plan || "basic";
  const planFeatures = PLAN_FEATURES[plan] || PLAN_FEATURES.basic;

  // Determine which modules the user's role can see
  let visibleModules;
  if (userRole === "superadmin" || userRole === "admin") {
    visibleModules = null; // see all
  } else if (userRole === "worker") {
    // Use member-specific permissions if available, otherwise default
    if (memberPermissions && memberPermissions.length > 0) {
      visibleModules = [...new Set([...WORKER_ALWAYS_VISIBLE, ...memberPermissions])];
    } else {
      visibleModules = WORKER_VISIBLE_DEFAULT;
    }
  } else if (userRole === "auditor") {
    visibleModules = AUDITOR_VISIBLE;
  } else {
    // supervisor sees all plan modules
    visibleModules = null;
  }

  const available = [];
  const locked = [];

  Object.entries(MODULES).forEach(([code, mod]) => {
    if (visibleModules && !visibleModules.includes(code)) return;
    if (planFeatures.includes(code)) {
      available.push({ code, ...mod, icon: MODULE_ICONS[code] || FileText });
    } else if (userRole === "admin" || userRole === "superadmin") {
      // Admins see locked modules so they know what's available
      locked.push({ code, ...mod, icon: MODULE_ICONS[code] || FileText });
    }
  });

  return { available, locked };
}

// ─── Inner layout (has access to PlanProvider) ───────────────────────────────
function LayoutInner({ children, currentPageName }) {
  const { currentVenue, venues, user, userRole, loading, switchVenue, membership } = useVenue();
  const { subscription } = usePlan();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);

  const isAdmin = userRole === "superadmin" || userRole === "admin";
  const { available, locked } = buildNav(userRole, subscription, membership?.permissions);

  // Alert count
  useEffect(() => {
    if (!currentVenue) return;
    hosys.entities.Alert.filter({ venue_id: currentVenue.id, status: "active" })
      .then(items => setAlertCount(items.length))
      .catch(() => {});
  }, [currentVenue]);

  useEffect(() => {
    if (!currentVenue) return;
    const unsub = hosys.entities.Alert.subscribe((event) => {
      if (event.data?.venue_id !== currentVenue.id) return;
      hosys.entities.Alert.filter({ venue_id: currentVenue.id, status: "active" })
        .then(items => setAlertCount(items.length))
        .catch(() => {});
    });
    return unsub;
  }, [currentVenue]);

  if (loading) return <QuickLoader />;

  // User authenticated but not assigned to any venue
  if (!currentVenue && userRole !== "superadmin" && !SUPERADMIN_EMAILS.includes(user?.email)) {
    return (
      <div className="min-h-screen bg-[#f5f6fa] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#7B2D3F]/10 flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-7 h-7 text-[#7B2D3F]" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Sense local assignat</h2>
          <p className="text-gray-500 text-sm mb-5">No estàs assignat a cap local. Contacta amb l'administrador perquè t'afegeixi a l'equip.</p>
          <a href="mailto:info@ho-sys.com" className="text-sm text-[#7B2D3F] font-medium underline underline-offset-2">Contactar Ho-Sys</a>
          <div className="mt-4 flex flex-col gap-2">
            <button 
              onClick={() => hosys.auth.redirectToLogin()} 
              className="text-sm text-[#7B2D3F] font-medium hover:text-[#5B1D2F] transition-colors"
            >
              Provar amb altre compte
            </button>
            <button onClick={() => hosys.auth.logout()} className="text-xs text-gray-400 hover:text-gray-600">Tancar sessió</button>
          </div>
        </div>
      </div>
    );
  }

  // Mobile bottom nav: always first 5 available modules
  const mobileNav = available.slice(0, 5);

  const renderNavItems = (items, onClick) =>
    items.map(item => (
      <NavItem
        key={item.code}
        name={item.label}
        page={item.page}
        icon={item.icon}
        isActive={currentPageName === item.page}
        onClick={onClick}
        badge={item.page === "NotificationCenter" ? alertCount : 0}
      />
    ));

  const renderSidebar = (onClick) => (
    <>
      <SectionLabel label="Operativa" />
      <nav className="flex flex-col gap-0.5">
        {renderNavItems(available, onClick)}
        {locked.length > 0 && (
          <React.Fragment key="locked-section">
            <div className="px-3 pt-3 pb-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600">No inclòs</p>
            </div>
            {locked.map(item => (
              <LockedNavItem key={item.code} name={item.label} icon={item.icon} />
            ))}
          </React.Fragment>
        )}
      </nav>

      {isAdmin && (
        <React.Fragment key="admin-section">
          <SectionLabel label="Administració" />
          <nav className="flex flex-col gap-0.5">
            {ADMIN_ITEMS.map(item => (
              <NavItem
                key={item.page}
                name={item.name}
                page={item.page}
                icon={item.icon}
                isActive={currentPageName === item.page}
                onClick={onClick}
                badge={0}
              />
            ))}
          </nav>
        </React.Fragment>
      )}

      {/* HOSYS AI — Academy & Compliance (special external-style link) */}
      <div className="px-3 pt-4 pb-1">
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-600">Suite Ho-Sys</p>
      </div>
      <Link
        to="/HosysAI"
        onClick={onClick}
        className="group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 relative bg-gradient-to-r from-slate-900 to-slate-800 text-white hover:from-slate-800 hover:to-slate-700 border border-cyan-500/20 shadow-lg shadow-slate-900/20"
        data-testid="nav-hosysai"
      >
        <div className="w-5 h-5 rounded-md bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-3 h-3 text-white" />
        </div>
        <span className="flex-1 truncate">HOSYS <span className="text-cyan-300">AI</span></span>
        <ChevronRight className="w-3.5 h-3.5 text-cyan-300 opacity-60 group-hover:opacity-100" />
      </Link>
    </>
  );

  return (
    <div className="min-h-screen bg-[#f5f6fa]">
      <TopHeader
        user={user}
        userRole={userRole}
        currentVenue={currentVenue}
        venues={venues}
        switchVenue={switchVenue}
        alertCount={alertCount}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        onProfileOpen={() => setProfileOpen(true)}
      />

      <div className="flex">
        <DesktopSidebar renderSidebar={renderSidebar} subscription={subscription} />
        <MobileSlideOut open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} renderSidebar={renderSidebar} />

        <main className="flex-1 pb-24 lg:pb-10 min-w-0">
          <div className="max-w-5xl mx-auto p-4 sm:p-6">{children}</div>
        </main>
      </div>

      <AIAssistant currentPageName={currentPageName} />
      <ProfileDialog user={user} open={profileOpen} onClose={() => setProfileOpen(false)} />
      <MobileBottomNav items={mobileNav} currentPageName={currentPageName} alertCount={alertCount} />
    </div>
  );
}

// ─── Root Layout ─────────────────────────────────────────────────────────────
// Pages that don't require authentication/layout (public pages)
const PUBLIC_PAGES = ["NFCClock", "Kiosk", "WorkerInvite"];
const WORKER_APP_PAGES = ["WorkerApp"];

export default function Layout({ children, currentPageName }) {
  if (PUBLIC_PAGES.includes(currentPageName)) {
    return <>{children}</>;
  }

  // HOSYS AI has its own layout — just wrap in VenueProvider
  if (currentPageName && currentPageName.startsWith("HosysAI")) {
    return <VenueProvider>{children}</VenueProvider>;
  }

  // WorkerApp — needs VenueContext but no full sidebar layout
  if (WORKER_APP_PAGES.includes(currentPageName)) {
    return (
      <VenueProvider>
        {children}
      </VenueProvider>
    );
  }

  return (
    <VenueProvider>
      <LayoutWithPlan currentPageName={currentPageName}>{children}</LayoutWithPlan>
    </VenueProvider>
  );
}

function LayoutWithPlan({ children, currentPageName }) {
  const { currentVenue, userRole, subscription: venueSubscription, loading } = useVenue();
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    // Si ja tenim subscripció del VenueContext, usar-la
    if (venueSubscription) {
      setSubscription(venueSubscription);
      return;
    }
    
    if (!currentVenue) return;
    const venueId = currentVenue.venue_id || currentVenue.id;
    if (!venueId) return;
    
    hosys.entities.Subscription.filter({ venue_id: venueId })
      .then(items => {
        setSubscription(items[0] || null);
      })
      .catch(() => {});
  }, [currentVenue, venueSubscription, userRole]);

  // Esperar que el VenueContext carregui
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#7B2D3F]/20 border-t-[#7B2D3F] rounded-full animate-spin"></div>
      </div>
    );
  }

  // Forçar userRole a "superadmin" si l'usuari està a la llista
  const effectiveUserRole = userRole || (SUPERADMIN_EMAILS.includes(currentVenue?.owner_email) ? "superadmin" : null);

  return (
    <PlanProvider subscription={subscription || venueSubscription} userRole={effectiveUserRole}>
      <LayoutInner currentPageName={currentPageName}>{children}</LayoutInner>
    </PlanProvider>
  );
}